import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user, clearAuth } from '@stores/authStore';
import { api } from '@services/api';
import { getStatusLabel, getPriorityLabel } from '@utils/labels';

interface DashboardStats {
  total_projects: number;
  total_issues: number;
  assigned_to_me: number;
  status_breakdown: Record<string, number>;
  priority_breakdown: Record<string, number>;
  recent_issues: Array<{
    id: number;
    project_code: string;
    issue_number: string;
    title: string;
    status: string;
    priority: string;
    created_at: string;
  }>;
}

interface Project {
  id: number;
  code: string;
  name: string;
  description: string;
  created_at: string;
  user_role?: string;
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state
  const [projectCode, setProjectCode] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectGitUrl, setProjectGitUrl] = useState('');

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }

    loadStats();
    loadProjects();
  }, []);

  const loadStats = async () => {
    const response = await api.get<any>('/api/dashboard/stats');
    if (response.success && response.data) {
      setStats(response.data);
    }
    setLoading(false);
  };

  const loadProjects = async () => {
    const response = await api.get<any>('/api/projects');
    if (response.success && response.data) {
      setProjects(response.data.items || []);
    }
  };

  const handleCreateProject = async (e: Event) => {
    e.preventDefault();

    if (!projectCode.trim() || !projectName.trim()) {
      alert('필수 항목을 입력해주세요');
      return;
    }

    const payload: any = {
      code: projectCode.trim().toUpperCase(),
      name: projectName.trim(),
      description: projectDescription.trim(),
    };

    if (projectGitUrl.trim()) {
      payload.git_repository_url = projectGitUrl.trim();
    }

    const response = await api.post<any>('/api/projects', payload);
    if (response.success) {
      setShowCreateModal(false);
      setProjectCode('');
      setProjectName('');
      setProjectDescription('');
      setProjectGitUrl('');
      loadProjects();
      loadStats();
    } else {
      alert(response.error || '프로젝트 생성 실패');
    }
  };

  const handleLogout = () => {
    clearAuth();
    route('/login');
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      review: 'bg-purple-100 text-purple-800',
      on_hold: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-600 text-white font-semibold',
      resolved: 'bg-green-100 text-green-800',
      updated: 'bg-cyan-100 text-cyan-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100 text-gray-600';
  };

  if (!isAuthenticated.value) {
    return null;
  }

  return (
    <div class="min-h-screen bg-gray-100">
      <header class="bg-white shadow">
        <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div class="flex items-center gap-3">
            <img src="/logo.svg" alt="DbitLabs Logo" class="h-8 w-8" />
            <h1 class="font-brand text-xl font-bold">DbitLabs : IssueManager</h1>
          </div>
          <div class="flex items-center gap-4">
            <button
              onClick={() => route('/issues')}
              class="text-sm text-gray-700 hover:text-gray-900"
            >
              전체 이슈
            </button>
            {(user.value?.is_admin || projects.some(p => p.user_role === 'pm' || p.user_role === 'developer')) && (
              <button
                onClick={() => route('/api-keys')}
                class="text-sm text-gray-700 hover:text-gray-900"
              >
                API Keys
              </button>
            )}
            {user.value?.is_admin && (
              <button
                onClick={() => route('/admin/telegram-users')}
                class="text-sm text-gray-700 hover:text-gray-900"
              >
                텔레그램 사용자
              </button>
            )}
            <span class="text-sm text-gray-600">
              {user.value?.name}
              {user.value?.is_admin && (
                <span class="ml-2 rounded bg-purple-100 px-2 py-1 text-xs text-purple-800">
                  관리자
                </span>
              )}
            </span>
            <button onClick={handleLogout} class="btn btn-secondary">
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <main class="mx-auto max-w-7xl px-4 py-8">
        {loading ? (
          <div class="text-center">로딩중...</div>
        ) : (
          <div>
            <div class="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div class="card">
                <h3 class="mb-2 text-sm text-gray-600">전체 프로젝트</h3>
                <p class="text-3xl font-bold">{stats?.total_projects || 0}</p>
              </div>
              <div class="card">
                <h3 class="mb-2 text-sm text-gray-600">전체 이슈</h3>
                <p class="text-3xl font-bold">{stats?.total_issues || 0}</p>
              </div>
              <div class="card">
                <h3 class="mb-2 text-sm text-gray-600">나에게 할당된 이슈</h3>
                <p class="text-3xl font-bold">{stats?.assigned_to_me || 0}</p>
              </div>
            </div>

            <div class="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div class="card">
                <h3 class="mb-4 text-lg font-semibold">상태 분포</h3>
                <div class="space-y-2">
                  {stats?.status_breakdown &&
                    Object.entries(stats.status_breakdown).map(([status, count]) => (
                      <div key={status} class="flex justify-between">
                        <span>{getStatusLabel(status)}</span>
                        <span class="font-semibold">{count}</span>
                      </div>
                    ))}
                </div>
              </div>

              <div class="card">
                <h3 class="mb-4 text-lg font-semibold">우선순위 분포</h3>
                <div class="space-y-2">
                  {stats?.priority_breakdown &&
                    Object.entries(stats.priority_breakdown).map(([priority, count]) => (
                      <div key={priority} class="flex justify-between">
                        <span>{getPriorityLabel(priority)}</span>
                        <span class="font-semibold">{count}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <div class="card mb-8">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold">프로젝트</h3>
                {user.value?.is_admin && (
                  <button onClick={() => setShowCreateModal(true)} class="btn btn-primary">
                    프로젝트 생성
                  </button>
                )}
              </div>
              <div class="space-y-2">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => route(`/projects/${project.id}`)}
                    class="flex cursor-pointer items-center justify-between border-b py-2 hover:bg-gray-50"
                  >
                    <div>
                      <span class="font-mono text-sm font-semibold text-blue-600">
                        {project.code}
                      </span>
                      <p class="font-medium">{project.name}</p>
                      {project.description && (
                        <p class="text-sm text-gray-600">{project.description}</p>
                      )}
                    </div>
                    <span class="text-xs text-gray-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
                {projects.length === 0 && (
                  <p class="text-center text-gray-500">아직 프로젝트가 없습니다</p>
                )}
              </div>
            </div>

            <div class="card">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold">최근 이슈</h3>
                <button onClick={() => route('/issues')} class="btn btn-primary">
                  전체 보기
                </button>
              </div>
              <div class="space-y-2">
                {stats?.recent_issues.map((issue) => (
                  <div
                    key={issue.id}
                    onClick={() => route(`/issues/${issue.id}`)}
                    class="flex cursor-pointer items-center justify-between border-b py-2 hover:bg-gray-50"
                  >
                    <div>
                      <span class="font-mono text-sm text-gray-600">{issue.issue_number}</span>
                      <p class="font-medium">{issue.title}</p>
                    </div>
                    <div class="flex gap-2">
                      <span class={`rounded px-2 py-1 text-xs ${getStatusBadge(issue.status)}`}>
                        {getStatusLabel(issue.status)}
                      </span>
                      <span class={`rounded px-2 py-1 text-xs ${getPriorityBadge(issue.priority)}`}>
                        {getPriorityLabel(issue.priority)}
                      </span>
                    </div>
                  </div>
                ))}
                {stats?.recent_issues.length === 0 && (
                  <p class="text-center text-gray-500">아직 이슈가 없습니다</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold">새 프로젝트 생성</h2>
            <form onSubmit={handleCreateProject}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">프로젝트 코드 *</label>
                <input
                  type="text"
                  value={projectCode}
                  onChange={(e) => setProjectCode((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="예: PROJ"
                  pattern="[A-Z0-9]+"
                  title="영문 대문자와 숫자만 사용 가능"
                  required
                />
                <p class="mt-1 text-xs text-gray-500">
                  영문 대문자와 숫자만 사용 가능 (자동 변환됨)
                </p>
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">프로젝트 이름 *</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="예: 나의 멋진 프로젝트"
                  required
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">설명</label>
                <textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={3}
                  placeholder="프로젝트에 대한 간단한 설명"
                />
              </div>

              <div class="mb-6">
                <label class="mb-1 block text-sm font-medium">Git 저장소 URL</label>
                <input
                  type="url"
                  value={projectGitUrl}
                  onChange={(e) => setProjectGitUrl((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="예: https://github.com/username/repository.git"
                />
                <p class="mt-1 text-xs text-gray-500">
                  선택사항. Git 연동 및 커밋 추적에 필요
                </p>
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  프로젝트 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
